import jwtDecode from 'jwt-decode';
import { sendHubspotSignupForm } from 'features/authentication/signup/utils/signup-hubspot';
import { AuthError } from 'features/authentication/utils/auth-error';
import { useCallback, useState } from 'react';
import { useAuthActions } from 'store/auth/use-auth-actions';
import { useRouteParam } from 'hooks/use-query/use-query';
import { useSplunkCallback } from 'hooks/use-splunk-callback/use-splunk-callback';
import { signupBaseSplunkData } from 'features/authentication/utils/constants';

type UseAuthenticateWithGoogleParams = {
    authMode: 'signin' | 'signup';
    urlUtmParams: Record<string, string | null>;
    setServerError?: (error: AuthError | undefined) => void;
};

export const useAuthenticateWithGoogle = ({
    authMode,
    urlUtmParams,
    setServerError,
}: UseAuthenticateWithGoogleParams) => {
    const [loading, setLoading] = useState(false);
    const { platformSignIn } = useAuthActions();
    const nextUrl = useRouteParam('next_url');
    const sendEvent = useSplunkCallback(signupBaseSplunkData);

    const login = useCallback(
        async (token: string) => {
            try {
                setLoading(true);
                const platform = 'google';
                const data = {
                    platform,
                    token,
                };

                await platformSignIn(data, nextUrl || '');
                const googleResponse = jwtDecode<{
                    email: string;
                    family_name: string;
                    given_name: string;
                }>(token);

                sendHubspotSignupForm(
                    googleResponse.email,
                    'google',
                    {
                        firstName: googleResponse.given_name,
                        lastName: googleResponse.family_name,
                        company: '',
                        phone: '',
                        password: '',
                    },
                    urlUtmParams,
                );

                sendEvent({
                    action: `${authMode}-success`,
                    method: platform,
                    email: googleResponse.email || '',
                });
            } catch (error) {
                setServerError?.(AuthError.fromUnknownError(error));
            } finally {
                setLoading(false);
            }
        },
        [nextUrl, platformSignIn, sendEvent, setServerError, urlUtmParams, authMode],
    );

    return {
        loginViaGoogle: login,
        loading,
    };
};
